<template>
  <div
    v-if="visible && phase !== 'done'"
    :class="['af-overlay', { 'af-fade-out': fadeOut }]"
    @click="phase === 'celebration' ? handleSkip() : null"
  >
    <!-- 警报阶段 -->
    <div v-if="phase === 'typing' || phase === 'countdown'" class="af-alert-container">
      <!-- 红色闪烁背景 -->
      <div class="af-red-flash" />
      <!-- 扫描线效果 -->
      <div class="af-scanlines" />
      <!-- 警报图标 -->
      <div class="af-warning-icon">
        <svg viewBox="0 0 24 24" fill="none" width="80" height="80">
          <path d="M12 2L1 21h22L12 2z" fill="none" stroke="#ff2020" stroke-width="2" />
          <path d="M12 9v5" stroke="#ff2020" stroke-width="2.5" stroke-linecap="round" />
          <circle cx="12" cy="17" r="1.2" fill="#ff2020" />
        </svg>
      </div>

      <!-- 打字机文本 -->
      <div v-if="phase === 'typing'" class="af-typing-area">
        <span class="af-typed-text">
          {{ warningText.slice(0, typedLength) }}
          <span class="af-cursor">|</span>
        </span>
      </div>

      <!-- 倒计时 -->
      <div v-if="phase === 'countdown'" class="af-countdown-wrapper">
        <div :key="countdown" class="af-countdown-number">
          {{ countdown }}
        </div>
        <div class="af-countdown-label">自毁倒计时</div>
      </div>

      <!-- 警报文字 -->
      <div class="af-alert-badge">
        <span class="af-alert-dot" />
        ALERT
      </div>
    </div>

    <!-- 庆祝阶段 -->
    <div v-if="phase === 'celebration'" class="af-celebration-container">
      <!-- 五彩纸屑 -->
      <div
        v-for="p in confettiPieces"
        :key="'c' + p.id"
        class="af-confetti"
        :style="{
          left: p.left + '%',
          animationDelay: p.delay + 's',
          animationDuration: p.duration + 's',
          backgroundColor: p.color,
          width: p.size + 'px',
          height: (p.size * 0.6) + 'px',
          transform: 'rotate(' + p.rotation + 'deg)',
        }"
      />

      <!-- 烟花 -->
      <div
        v-for="f in fireworks"
        :key="'f' + f.id"
        class="af-firework"
        :style="{
          left: f.left + '%',
          top: f.top + '%',
          animationDelay: f.delay + 's',
        }"
      >
        <div
          v-for="i in 12"
          :key="i"
          class="af-firework-particle"
          :style="{ transform: 'rotate(' + (i - 1) * 30 + 'deg)' }"
        />
      </div>

      <!-- 庆祝文字 -->
      <div class="af-celebration-text">
        <div class="af-happy-title">
          <span class="af-emoji">🎉</span>
          愚人节快乐
          <span class="af-emoji">🎉</span>
        </div>
        <div class="af-happy-name">{{ userName }}</div>
        <div class="af-happy-wish">祝你天天开心，工作顺心</div>
      </div>

      <div class="af-skip-hint">点击任意位置关闭</div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const STORAGE_KEY = 'april_fools_2026_shown'
const WARNING_TEXT = '检测到危险程序，警报，警报，电脑将在五秒后开启自毁程序'
const COUNTDOWN_SECONDS = 5
const CELEBRATION_DURATION = 8000

/** 判断今天是否是愚人节 */
function isAprilFools() {
  const now = new Date()
  return now.getMonth() === 3 && now.getDate() === 1
}

const CONFETTI_COLORS = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6ec7', '#a855f7', '#f97316']

export default {
  name: 'AprilFools',
  data() {
    return {
      phase: 'typing', // typing | countdown | celebration | done
      typedLength: 0,
      countdown: COUNTDOWN_SECONDS,
      visible: false,
      fadeOut: false,
      warningText: WARNING_TEXT,
      typingTimer: null,
      countdownTimer: null,
      celebrationTimer: null,
    }
  },
  computed: {
    ...mapGetters(['name', 'user_id']),
    userName() {
      return this.name || '朋友'
    },
    shouldShow() {
      const dateOk = isAprilFools()
      const userId = this.user_id
      const key = `${STORAGE_KEY}_${userId}`
      const alreadyShown = localStorage.getItem(key)
      console.log('[AprilFools] dateOk:', dateOk, 'userId:', userId, 'alreadyShown:', alreadyShown)
      if (!dateOk) return false
      if (!userId) return false
      return !alreadyShown
    },
    confettiPieces() {
      return Array.from({ length: 80 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 2 + Math.random() * 3,
        color: CONFETTI_COLORS[i % 7],
        size: 6 + Math.random() * 10,
        rotation: Math.random() * 360,
      }))
    },
    fireworks() {
      return Array.from({ length: 6 }, (_, i) => ({
        id: i,
        left: 10 + Math.random() * 80,
        top: 10 + Math.random() * 60,
        delay: Math.random() * 4,
      }))
    },
  },
  watch: {
    phase(val) {
      if (val === 'typing') {
        this.startTyping()
      } else if (val === 'countdown') {
        this.startCountdown()
      } else if (val === 'celebration') {
        this.startCelebration()
      }
    },
  },
  mounted() {
    if (this.shouldShow) {
      this.visible = true
      this.startTyping()
    }
  },
  beforeDestroy() {
    clearTimeout(this.typingTimer)
    clearTimeout(this.countdownTimer)
    clearTimeout(this.celebrationTimer)
  },
  methods: {
    startTyping() {
      const tick = () => {
        if (this.phase !== 'typing' || !this.visible) return
        if (this.typedLength >= WARNING_TEXT.length) {
          this.typingTimer = setTimeout(() => {
            this.phase = 'countdown'
          }, 800)
          return
        }
        this.typedLength++
        this.typingTimer = setTimeout(tick, 80)
      }
      tick()
    },
    startCountdown() {
      const tick = () => {
        if (this.phase !== 'countdown' || !this.visible) return
        if (this.countdown <= 0) {
          this.phase = 'celebration'
          return
        }
        this.countdown--
        this.countdownTimer = setTimeout(tick, 1000)
      }
      this.countdownTimer = setTimeout(tick, 1000)
    },
    startCelebration() {
      this.celebrationTimer = setTimeout(() => {
        this.close()
      }, CELEBRATION_DURATION)
    },
    handleSkip() {
      clearTimeout(this.celebrationTimer)
      this.close()
    },
    close() {
      this.fadeOut = true
      setTimeout(() => {
        this.phase = 'done'
        this.visible = false
        if (this.user_id) {
          localStorage.setItem(`${STORAGE_KEY}_${this.user_id}`, '1')
        }
      }, 500)
    },
  },
}
</script>

<style src="./style.css"></style>
